import React, { useState, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import './css/login.css'
import { toast } from 'react-toastify';
import { fetchCart } from '../redux/shopSlice'
import { useDispatch } from 'react-redux'
import { AppContext } from '../context/appContext'
import axios from 'axios'
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getUserData, backendUrl, isLoggedIn, setIsLoggedIn, userData } = useContext(AppContext);

  const [state, setState] = useState('Login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const passwordValue = watch('password');

  useEffect(() => {
    if (isLoggedIn === null || userData === null) return;
    if (isLoggedIn && userData) {
      if (userData.isAccountVerified) {
        navigate('/');
      } else {
        navigate('/email-verify');
      }
    }
  }, [isLoggedIn, userData, navigate]);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (state === 'Login') {
        const res = await axios.post(`${backendUrl}/api/auth/login`, data, { withCredentials: true });
        if (res.data.success) {
          setIsLoggedIn(true);
          getUserData();
          dispatch(fetchCart({ page: 0, limit: 10 }));
          toast.success(res.data.message);
        } else {
          toast.error(res.data.message);
        }
      } else {
        const res = await axios.post(`${backendUrl}/api/auth/register`, data, { withCredentials: true });
        if (res.data.success) {
          setIsLoggedIn(true);
          getUserData();
          toast.success('Account created successfully!');
        } else {
          toast.error(res.data.message);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page">
      <Paper elevation={10} className="login-container">
        <Typography variant="h4" className="login-title">
          {state === 'Sign Up' ? 'Create Account' : 'Welcome Back'}
        </Typography>
        <Typography variant="body2" className="login-subtitle">
          {state === 'Sign Up'
            ? 'Create your account to get started'
            : 'Login to continue shopping'}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className="login-fields">
            {state === 'Sign Up' && (
              <TextField
                fullWidth
                variant="outlined"
                label="Full Name"
                {...register('name', { required: 'Full name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
            <TextField
              fullWidth
              variant="outlined"
              label="Email Address"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Enter a valid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              fullWidth
              variant="outlined"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end" sx={{ color: '#9ca3af' }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {state === 'Sign Up' && (
              <TextField
                fullWidth
                variant="outlined"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                {...register('confirmPassword', {
                  required: 'Confirm your password',
                  validate: (value) =>
                    value === passwordValue || 'Passwords do not match',
                })}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                        sx={{ color: '#9ca3af' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {state === 'Login' && (
            <Typography onClick={() => navigate('/reset-password')} className="forgot-password">
              Forgot Password?
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            className="continue-btn"
            type="submit"
            disabled={loading}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : state}
          </Button>
        </form>

        {state === 'Login' ? (
          <Typography className="login-text">
            Don’t have an account?{' '}
            <span onClick={() => setState('Sign Up')}>Sign Up</span>
          </Typography>
        ) : (
          <Typography className="login-text">
            Already have an account?{' '}
            <span onClick={() => setState('Login')}>Login Here</span>
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Login;